import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import restaurantService, {
  formatSpaceLabel,
  MenuItem,
  MenuRecommendationsPayload,
  OrderDetails,
  OrderItem,
  OrderDetailsResponse,
  OrderFeedbackSummary,
  PlanFeaturesPayload,
} from '../../services/restaurantService';
import { hasCustomerFeature } from '../../utils/planFeatures';
import { startOrResumeGuestOrderPayment } from '../../utils/paymentCheckout';
import { abandonGuestPayment, getOrderPayment, isOpenPaymentStatus } from '../../services/paymentService';
import cartService from '../../services/cartService';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import LoginModal from '../LoginModal/LoginModal';
import Toast from '../Toast/Toast';
import OrderFeedbackPanel from '../OrderFeedback/OrderFeedbackPanel';
import OrderFeedbackModal from '../OrderFeedback/OrderFeedbackModal';
import MenuRecommendationsStrip from '../common/MenuRecommendationsStrip/MenuRecommendationsStrip';
import {
  isCompletedOrderStatus,
  isPaymentIncompleteStatus,
  isTerminalOrderStatus,
  itemStatusBadgeClass,
  itemStatusLabel,
  keepFreshGuestOrderStatus,
} from '../../utils/orderStatus';
import {
  deliveryHeadline,
  deliveryHint,
  deliveryStepIndex,
  isTerminalRiderStatus,
  keepFreshDeliveryTrackingStatus,
  resolveDeliveryGuestOrderStatus,
} from '../../utils/deliveryGuestStatus';
import { guestPaidPresentation, guestPaymentMethodLabel } from '../../utils/guestPaymentMethods';
import { useRealtime } from '../../contexts/RealtimeContext';
import useOrderPolling from '../../hooks/useOrderPolling';
import { useEmailSignInPrompt } from '../../hooks/useEmailSignInPrompt';
import { captureEmailSignInPrompt, clearCapturedEmailSignInPrompt } from '../../utils/emailSignInPrompt';
import { hasPendingSocialAuthError } from '../../utils/socialAuth';
import { useAppSelector } from '../../store/hooks';
import { useKioskMode } from '../../contexts/KioskModeContext';
import {
  kioskConfirmationPath,
  kioskMenuPath,
  kioskOrdersPath,
  mapRestaurantPathToKiosk,
  resolveKioskPaymentRestroId,
  shouldRedirectRestaurantPathToKiosk,
} from '../Kiosk/kioskPaths';
import { useEnabledGames } from '../Games/useEnabledGames';
import deliveryService, { formatDeliveryAddressLine } from '../../services/deliveryService';
import { formatPhone, restaurantTelHref } from '../Footer/footerUtils';
import { requestCaptainAssistanceOpen } from '../../utils/captainPanelEvents';
import { DINE_IN_ORDER_TYPE } from '../../virtual-captain/types/events';
import { useKioskGuestOrderingPath } from '../Kiosk/useKioskGuestOrderingPath';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import '../shared/ordersPageShell.css';
import './OrderDetails.css';
import OrderLineOptions, { OrderKitchenNote } from '../common/OrderLineOptions/OrderLineOptions';
import OrderDetailsSkeleton from './OrderDetailsSkeleton';
import OrderUnavailableState from '../shared/OrderUnavailableState';
import GuestInvoiceDownload from '../common/GuestInvoiceDownload/GuestInvoiceDownload';
import { guestItemGroups } from '../../utils/guestItemGroups';
import { GuestInvoiceDownloadPayload } from '../../utils/guestInvoiceDownload';

type DeliveryTracking = {
  success?: boolean;
  status?: string;
  payMethod?: string;
  drop?: {
    line?: string;
    area?: string;
    locality?: string;
    pincode?: string;
    fullName?: string;
    phone?: string;
    instructions?: string;
  };
  expectedDeliverAt?: string;
  expectedPrepAt?: string;
  acceptDeadlineAt?: string;
};

const DELIVERY_STEPS = [
  { key: 'placed', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready' },
  { key: 'at_restaurant', label: 'At the restaurant' },
  { key: 'picked', label: 'Order picked' },
  { key: 'on_way', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
] as const;

const formatClock = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const OrderDetailsPage: React.FC = () => {
  const { appName, orderId } = useParams<{ appName: string; orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, authReady, user: signedInUser } = useAppSelector((state) => state.user);
  const {
    enabled: kioskEnabled,
    role: kioskRole,
    beginTableDineInFeedbackWait,
    awaitingFeedbackPhase,
  } = useKioskMode();
  const guestOrderingPath = useKioskGuestOrderingPath();
  const guestOrderType = useGuestOrderType(appName);
  const browseMenuPath = useMemo(() => {
    if (!appName) return '/restaurants';
    if (kioskEnabled) {
      return withGuestOrderTypeQuery(kioskMenuPath(appName), appName, guestOrderType);
    }
    return `/restaurants/${appName}/menu`;
  }, [appName, guestOrderType, kioskEnabled]);

  useEffect(() => {
    if (!appName || kioskEnabled) return;
    if (!location.pathname.startsWith(`/restaurants/${appName}/orders/`)) return;
    if (shouldRedirectRestaurantPathToKiosk(appName)) {
      navigate(mapRestaurantPathToKiosk(appName, `${location.pathname}${location.search}`), {
        replace: true,
      });
    }
  }, [appName, kioskEnabled, location.pathname, location.search, navigate]);

  const [emailSignInRequested] = useState(() => captureEmailSignInPrompt());
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>('Restaurant');
  const [restaurantPhone, setRestaurantPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [feedbackSummary, setFeedbackSummary] = useState<OrderFeedbackSummary | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackPanelKey, setFeedbackPanelKey] = useState(0);
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(undefined);
  const { hasEnabledGames } = useEnabledGames(
    hasCustomerFeature(planFeatures, 'in_app_games') ? appName : undefined
  );
  const [activePromotions, setActivePromotions] = useState<Array<{ id: number; name: string; description?: string; discountPreview?: string }>>([]);
  const [showAllOffers, setShowAllOffers] = useState(false);
  const [orderPromotionPreview, setOrderPromotionPreview] = useState<
    OrderDetailsResponse['orderPromotionPreview'] | undefined
  >(undefined);
  const [itemPromotionBadges, setItemPromotionBadges] = useState<
    NonNullable<OrderDetailsResponse['itemPromotionBadges']>
  >({});
  const [publicCoupons, setPublicCoupons] = useState<
    Array<{ id: number; couponCode?: string; couponType?: string; discount?: number; minAmount?: number; description?: string }>
  >([]);
  const [showLoginModal, setShowLoginModal] = useState(() => hasPendingSocialAuthError());
  const [menuRecommendations, setMenuRecommendations] = useState<MenuRecommendationsPayload | null>(null);
  const [canPayOnline, setCanPayOnline] = useState(false);
  const [onlinePaidAmount, setOnlinePaidAmount] = useState<number | null>(null);
  const [onlineBalanceDue, setOnlineBalanceDue] = useState<number | null>(null);
  const [paying, setPaying] = useState(false);
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null);
  const [cancellingPayment, setCancellingPayment] = useState(false);
  const [deliveryTracking, setDeliveryTracking] = useState<DeliveryTracking | null>(null);
  const [invoiceDownload, setInvoiceDownload] = useState<GuestInvoiceDownloadPayload | null>(null);
  const stepsScrollRef = useRef<HTMLDivElement>(null);

  const pendingEmailSignIn =
    authReady && !isAuthenticated && (emailSignInRequested || showLoginModal);

  const promptEmailSignIn = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  const dismissEmailSignIn = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  useEmailSignInPrompt({
    onPrompt: promptEmailSignIn,
    onAlreadySignedIn: dismissEmailSignIn,
  });

  const handleNotify = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToastMessage(message);
    setToastType(type);
  }, []);

  useEffect(() => {
    const uo = orderDetails?.userOrder;
    if (!kioskEnabled || kioskRole !== 'TABLE_KIOSK' || !appName || !uo) {
      return;
    }
    if (uo.orderType !== 1) {
      return;
    }
    if (!isCompletedOrderStatus(uo.orderStatus)) {
      return;
    }
    beginTableDineInFeedbackWait(appName);
  }, [
    appName,
    beginTableDineInFeedbackWait,
    kioskEnabled,
    kioskRole,
    orderDetails?.userOrder?.orderStatus,
    orderDetails?.userOrder?.orderType,
  ]);

  const { subscribeOrderStatus, subscribeOrderWaitTime } = useRealtime();

  const applyOrderResponse = useCallback((response: OrderDetailsResponse) => {
    if (response.orderDetails) {
      setOrderDetails((prev) => {
        const incoming = response.orderDetails!;
        if (!prev) return incoming;
        const keptStatus = keepFreshGuestOrderStatus(
          prev.userOrder?.orderStatus,
          incoming.userOrder?.orderStatus
        );
        if (keptStatus === incoming.userOrder?.orderStatus) {
          return incoming;
        }
        return {
          ...incoming,
          userOrder: { ...incoming.userOrder, orderStatus: keptStatus },
        };
      });
    }
    if (response.feedbackSummary) {
      setFeedbackSummary(response.feedbackSummary);
    }
    if (response.activePromotions) {
      setActivePromotions(response.activePromotions);
    }
    setOrderPromotionPreview(response.orderPromotionPreview);
    setItemPromotionBadges(response.itemPromotionBadges ?? {});
    setMenuRecommendations(response.menuRecommendations ?? null);
    setCanPayOnline(Boolean(response.canPayOnline));
    setOnlinePaidAmount(
      typeof response.onlinePaidAmount === 'number' ? response.onlinePaidAmount : null
    );
    setOnlineBalanceDue(
      typeof response.onlineBalanceDue === 'number' ? response.onlineBalanceDue : null
    );
    if (response.restaurantName) {
      setRestaurantName(response.restaurantName);
    }
    if (response.restaurantPhone) {
      setRestaurantPhone(response.restaurantPhone);
    }
    if (response.invoiceDownload) {
      setInvoiceDownload(response.invoiceDownload);
    }
  }, []);

  useEffect(() => {
    if (!appName || !orderId || !authReady) {
      return;
    }

    if (!isAuthenticated) {
      if (emailSignInRequested) {
        setShowLoginModal(true);
      }
      setLoading(false);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError('');
        const response: OrderDetailsResponse = await restaurantService.getOrderDetails(
          appName,
          parseInt(orderId, 10)
        );

        if (response.success && response.orderDetails) {
          applyOrderResponse(response);
        } else {
          setError(response.message || 'Order not found');
        }
      } catch (err: any) {
        if (err.response?.status === 401 && emailSignInRequested) {
          setShowLoginModal(true);
          setError('');
        } else {
          setError(err.response?.data?.message || 'Error fetching order details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [appName, orderId, authReady, isAuthenticated, emailSignInRequested, applyOrderResponse]);

  useEffect(() => {
    if (!appName || !orderId || orderDetails?.userOrder?.orderType !== 2) {
      return;
    }
    deliveryService
      .tracking(appName, parseInt(orderId, 10))
      .then((data) => {
        if (data?.success) {
          setDeliveryTracking(data);
        }
      })
      .catch(() => undefined);
  }, [appName, orderId, orderDetails?.userOrder?.orderType]);

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!appName) return;
      try {
        const response = await restaurantService.getRestaurant(appName);
        if (response.success && response.restaurant) {
          setRestaurantName(response.restaurant.restaurantName || 'Restaurant');
          const phone = formatPhone(response.restaurant);
          if (phone) {
            setRestaurantPhone(phone);
          }
        }
        if (response.features) {
          setPlanFeatures(response.features);
        }
      } catch {
        // use default
      }
    };
    fetchRestaurant();
  }, [appName]);

  useEffect(() => {
    if (!appName) return;
    restaurantService.getPublicCoupons(appName).then((couponRes) => {
      if (couponRes.success) {
        setPublicCoupons(couponRes.coupons || []);
      }
    });
    cartService.getActivePromotions(appName).then((promoRes) => {
      if (promoRes.success && promoRes.promotions?.length) {
        setActivePromotions((prev) => (prev.length > 0 ? prev : promoRes.promotions));
      }
    });
  }, [appName]);

  const applicableOffers = useMemo(() => {
    const coupons = publicCoupons.map((c) => ({
      id: `coupon-${c.id}`,
      label: c.couponCode || 'Offer',
      description: c.description || '',
      badge: c.couponCode || 'DEAL',
    }));
    const promos = activePromotions.map((p) => ({
      id: `promo-${p.id}`,
      label: p.name,
      description: p.discountPreview || p.description || '',
      badge: p.discountPreview || 'PROMO',
    }));
    return [...coupons, ...promos];
  }, [publicCoupons, activePromotions]);

  const pollOrderDetails = React.useCallback(async () => {
    if (!appName || !orderId) return;
    try {
      const response = await restaurantService.getOrderDetails(appName, parseInt(orderId, 10));
      if (response.success && response.orderDetails) {
        applyOrderResponse(response);
        if (response.orderDetails.userOrder?.orderType === 2) {
          const tracking = await deliveryService.tracking(appName, parseInt(orderId, 10));
          if (tracking?.success) {
            setDeliveryTracking((prev) => ({
              ...tracking,
              status: keepFreshDeliveryTrackingStatus(prev?.status, tracking.status),
            }));
          }
        }
      }
    } catch {
      // silent refresh
    }
  }, [appName, orderId, applyOrderResponse]);

  const pollOrderDetailsRef = useRef(pollOrderDetails);
  pollOrderDetailsRef.current = pollOrderDetails;

  const handleLoginSuccess = useCallback(async () => {
    clearCapturedEmailSignInPrompt();
    setShowLoginModal(false);
    if (!appName || !orderId) return;
    setLoading(true);
    setError('');
    try {
      const response = await restaurantService.getOrderDetails(appName, parseInt(orderId, 10));
      if (response.success && response.orderDetails) {
        applyOrderResponse(response);
      } else {
        setError(response.message || 'Order not found');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Error fetching order details');
    } finally {
      setLoading(false);
    }
  }, [appName, orderId, applyOrderResponse]);

  const handleFeedbackSubmitted = useCallback(
    (summary: OrderFeedbackSummary) => {
      setFeedbackSummary(summary);
      setShowFeedbackModal(false);
      setFeedbackPanelKey((key) => key + 1);
      pollOrderDetails();
    },
    [pollOrderDetails]
  );

  useEffect(() => {
    if (!orderId) return undefined;
    const oid = parseInt(orderId, 10);
    return subscribeOrderStatus((payload) => {
      if (Number(payload.orderId) !== oid) return;
      const nextStatus = payload.orderStatus || payload.status;

      if (payload.deliveryStatus) {
        setDeliveryTracking((prev) => ({
          ...(prev || {}),
          success: true,
          status: keepFreshDeliveryTrackingStatus(prev?.status, payload.deliveryStatus),
        }));
      }

      const riderTerminal = isTerminalRiderStatus(payload.deliveryStatus);
      if (riderTerminal && !payload.userOrderItems) {
        const mapped =
          String(payload.deliveryStatus).toUpperCase() === 'FAILED'
            ? 'DELIVERY_FAILED'
            : String(payload.deliveryStatus).toUpperCase() === 'DELIVERED'
              ? 'DELIVERY_ORDER_COMPLETED'
              : 'DELIVERY_CANCELLED';
        if (!nextStatus) {
          setOrderDetails((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              userOrder: {
                ...prev.userOrder,
                orderStatus: keepFreshGuestOrderStatus(prev.userOrder.orderStatus, mapped),
              },
            };
          });
        }
      }

      if (payload.userOrderItems) {
        try {
          const patchList = JSON.parse(payload.userOrderItems) as Array<{
            id?: number;
            itemName?: string;
            status?: string;
            quantity?: number;
            active?: boolean;
          }>;
          if (Array.isArray(patchList) && patchList.length > 0) {
            setOrderDetails((prev) => {
              if (!prev) return prev;
              const items = [...(prev.userOrderItemsList || [])];
              for (const patch of patchList) {
                if (patch.id == null) continue;
                const idx = items.findIndex((item) => item.id === patch.id);
                if (idx >= 0) {
                  items[idx] = { ...items[idx], ...patch };
                }
              }
              return {
                ...prev,
                userOrderItemsList: items,
                userOrder: nextStatus
                  ? {
                      ...prev.userOrder,
                      orderStatus: keepFreshGuestOrderStatus(prev.userOrder.orderStatus, nextStatus),
                    }
                  : prev.userOrder,
              };
            });
          }
        } catch {
          // fall through to server refresh
        }
      } else if (nextStatus) {
        setOrderDetails((prev) => {
          if (!prev) return prev;
          const waitPatch =
            payload.approxWaitTime != null && payload.approxWaitTime > 0
              ? { approxWaitTime: payload.approxWaitTime }
              : {};
          return {
            ...prev,
            userOrder: {
              ...prev.userOrder,
              orderStatus: keepFreshGuestOrderStatus(prev.userOrder.orderStatus, nextStatus),
              ...waitPatch,
            },
          };
        });
      }

      const appliedStatus = nextStatus || (riderTerminal ? String(payload.deliveryStatus) : '');
      if (
        appliedStatus &&
        (isTerminalOrderStatus(appliedStatus) || isTerminalRiderStatus(payload.deliveryStatus))
      ) {
        return;
      }

      // Staff pushes often omit item rows; delivery sync runs after the CometD message.
      void pollOrderDetailsRef.current();
    });
  }, [orderId, subscribeOrderStatus]);

  useEffect(() => {
    if (!orderId) return undefined;
    const oid = parseInt(orderId, 10);
    return subscribeOrderWaitTime((payload) => {
      if (Number(payload.orderId) !== oid) return;
      if (!payload.approxWaitTime || payload.approxWaitTime <= 0) return;
      setOrderDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          userOrder: { ...prev.userOrder, approxWaitTime: payload.approxWaitTime },
        };
      });
      void pollOrderDetailsRef.current();
    });
  }, [orderId, subscribeOrderWaitTime]);

  useEffect(() => {
    const wrap = stepsScrollRef.current;
    if (!wrap) return;
    const current = wrap.querySelector<HTMLElement>('li.is-current');
    if (!current) return;
    const left = current.offsetLeft - (wrap.clientWidth - current.offsetWidth) / 2;
    wrap.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [orderDetails?.userOrder?.orderStatus, deliveryTracking?.status]);

  const orderTerminal = Boolean(
    orderDetails?.userOrder?.orderStatus &&
      isTerminalOrderStatus(orderDetails.userOrder.orderStatus)
  );
  useOrderPolling(pollOrderDetails, Boolean(appName && orderId && orderDetails && !orderTerminal));

  useEffect(() => {
    if (!orderId || !feedbackSummary) return;
    const dismissed = sessionStorage.getItem(`feedback-modal-dismissed-${orderId}`);
    if (
      feedbackSummary.eligible &&
      !feedbackSummary.submitted &&
      !dismissed
    ) {
      setShowFeedbackModal(true);
    }
  }, [orderId, feedbackSummary]);

  const handleDismissFeedbackModal = () => {
    if (orderId) {
      sessionStorage.setItem(`feedback-modal-dismissed-${orderId}`, '1');
    }
    setShowFeedbackModal(false);
  };

  const getStatusLabel = (status: string): { label: string; className: string } => {
    const statusMap: Record<string, { label: string; className: string }> = {
      ORDER_INITIATED: { label: 'Order Initiated', className: 'status-modified' },
      ORDER_CONFIRMED: { label: 'Order Confirmed', className: 'status-ready' },
      ORDER_PLACED: { label: 'Order Placed', className: 'status-placed' },
      ORDER_PREPARING: { label: 'Order Preparing', className: 'status-modified' },
      ORDER_PREPARED: { label: 'Order Prepared', className: 'status-ready' },
      ORDER_SERVED: { label: 'Order Served', className: 'status-placed' },
      ORDER_COMPLETED: { label: 'Order Completed', className: 'status-placed' },
      ORDER_CANCELLED: { label: 'Order Cancelled', className: 'status-cancelled' },
      ORDER_FAILED: { label: 'Order Failed', className: 'status-cancelled' },
      ORDER_PICKED: { label: 'Order Picked', className: 'status-ready' },
      ORDER_MODIFIED: { label: 'Order Modified', className: 'status-modified' },
      PENDING_RESTAURANT_ACCEPTANCE: { label: 'Awaiting restaurant', className: 'status-modified' },
      PAYMENT_DONE: { label: 'Paid', className: 'status-ready' },
      DELIVERY_INITIATED: { label: 'Order confirmed', className: 'status-ready' },
      DELIVERY_ASSIGNED: { label: 'Rider assigned', className: 'status-ready' },
      DELIVERY_AT_RESTAURANT: { label: 'At the restaurant', className: 'status-ready' },
      DELIVERY_STARTED: { label: 'Out for delivery', className: 'status-ready' },
      DELIVERY_COMPLETED: { label: 'Delivered', className: 'status-placed' },
      DELIVERY_ORDER_COMPLETED: { label: 'Delivered', className: 'status-placed' },
      DELIVERY_FAILED: { label: 'Delivery failed', className: 'status-cancelled' },
      DELIVERY_CANCELLED: { label: 'Delivery cancelled', className: 'status-cancelled' },
    };
    if (!statusMap[status]) {
      const formattedLabel = status
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return { label: formattedLabel, className: 'status-ready' };
    }
    return statusMap[status];
  };

  const getOrderTypeLabel = (orderType: number): string => {
    switch (orderType) {
      case 1:
        return 'DINE-IN';
      case 2:
        return 'DELIVERY';
      case 3:
        return 'TAKE-AWAY';
      default:
        return 'ORDER';
    }
  };

  const formatCurrency = (amount: number, currencySymbol: string = '₹'): string => {
    let decodedSymbol = currencySymbol;
    if (currencySymbol && currencySymbol.includes('&#')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = currencySymbol;
      decodedSymbol = tempDiv.textContent || tempDiv.innerText || '₹';
    }
    return `${decodedSymbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleShareOrder = (pin: string, oid: number, app: string) => {
    const shareUrl = `${window.location.origin}/restaurants/${app}/join-order?pin=${pin}&orderId=${oid}`;

    if (navigator.share) {
      navigator.share({
        title: 'Join my order',
        text: `Join my order using PIN: ${pin}`,
        url: shareUrl,
      }).catch(() => {
        navigator.clipboard.writeText(shareUrl);
        handleNotify('Link copied to clipboard!', 'success');
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      handleNotify('Link copied to clipboard!', 'success');
    }
  };

  const bottomBarConfig = useMemo(() => {
    const uo = orderDetails?.userOrder;
    if (!uo) {
      return {
        visible: false,
        showPay: false,
        showCallCaptain: false,
        showSharePin: false,
        payAmount: 0,
      };
    }
    const billing = orderDetails?.billingDetails;
    const payAmount =
      onlineBalanceDue != null && onlineBalanceDue > 0
        ? onlineBalanceDue
        : billing?.grandTotal != null && billing.grandTotal > 0
          ? billing.grandTotal
          : uo.grandTotal ?? 0;
    const showCallCaptain =
      uo.orderType === DINE_IN_ORDER_TYPE && !isTerminalOrderStatus(uo.orderStatus);
    const showSharePin = Boolean(
      uo.isSharedOrder && uo.sharePin && !isTerminalOrderStatus(uo.orderStatus)
    );
    const showPay =
      canPayOnline && payAmount > 0 && !isCompletedOrderStatus(uo.orderStatus);
    const visible = showPay || showCallCaptain || showSharePin;
    return { visible, showPay, showCallCaptain, showSharePin, payAmount };
  }, [orderDetails, canPayOnline, onlineBalanceDue]);

  useEffect(() => {
    if (!bottomBarConfig.visible) {
      return undefined;
    }
    document.body.classList.add('order-details-bottom-bar-visible');
    return () => {
      document.body.classList.remove('order-details-bottom-bar-visible');
    };
  }, [bottomBarConfig.visible]);

  useEffect(() => {
    const order = orderDetails?.userOrder;
    const fullyPaid =
      order?.paymentStatus === 'PAID' &&
      (onlineBalanceDue == null || onlineBalanceDue <= 0);
    if (!order?.id || !order.restroId || fullyPaid) {
      setOpenPaymentId(null);
      return undefined;
    }
    let cancelled = false;
    getOrderPayment(order.id, order.restroId).then((payment) => {
      if (cancelled) return;
      setOpenPaymentId(payment && isOpenPaymentStatus(payment.status) ? payment.paymentId : null);
    });
    return () => {
      cancelled = true;
    };
  }, [
    orderDetails?.userOrder?.id,
    orderDetails?.userOrder?.restroId,
    orderDetails?.userOrder?.paymentStatus,
    onlineBalanceDue,
  ]);

  const autoPayKioskRef = useRef(false);
  const autoPayOnlineFromCheckout = Boolean(
    (location.state as { autoPayOnline?: boolean } | null)?.autoPayOnline
  );

  const handleCancelOpenPayment = async () => {
    if (!openPaymentId || cancellingPayment) return;
    setCancellingPayment(true);
    try {
      await abandonGuestPayment(openPaymentId, 'CANCELLED');
      setOpenPaymentId(null);
      handleNotify('Payment cancelled. You can add items or pay another way.', 'success');
    } catch {
      handleNotify('Could not cancel the payment. Please try again.', 'error');
    } finally {
      setCancellingPayment(false);
    }
  };

  const handlePayOnline = async () => {
    if (!orderDetails?.userOrder || paying) return;
    setPaying(true);
    try {
      const orderId = orderDetails.userOrder.id;
      const returnUrl =
        kioskEnabled && appName
          ? kioskRole === 'TABLE_KIOSK'
            ? `${window.location.origin}${kioskOrdersPath(appName, orderId)}`
            : `${window.location.origin}${kioskConfirmationPath(appName)}?orderId=${orderId}`
          : undefined;
      const paymentRestroId =
        resolveKioskPaymentRestroId(orderDetails.userOrder.restroId) ?? orderDetails.userOrder.restroId;
      const result = await startOrResumeGuestOrderPayment({
        restroId: paymentRestroId,
        orderId,
        restaurantName,
        orderCode: orderDetails.userOrder.orderCode || orderId,
        returnUrl,
      });
      if ('redirected' in result) {
        return;
      }
      handleNotify('Payment received. Thank you!', 'success');
      if (kioskEnabled && appName && kioskRole !== 'TABLE_KIOSK') {
        navigate(kioskConfirmationPath(appName), {
          replace: true,
          state: { orderId, token: String(orderId) },
        });
        return;
      }
      const refreshed = await restaurantService.getOrderDetails(appName!, orderId);
      if (refreshed.success) {
        applyOrderResponse(refreshed);
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
        ?.message || (err as { message?: string })?.message || 'Payment failed';
      if (message !== 'Payment cancelled') {
        handleNotify(message, 'error');
      }
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    if (autoPayKioskRef.current || paying) {
      return;
    }
    const uo = orderDetails?.userOrder;
    if (!uo || !appName) {
      return;
    }
    const payFirstOnline =
      (uo.orderType === 2 || uo.orderType === 3) &&
      uo.orderStatus === 'PAYMENT_INITIATED' &&
      uo.paymentMethod === 'ONLINE_PAYMENT';
    const needsPay = kioskEnabled
      ? autoPayOnlineFromCheckout ||
        (isPaymentIncompleteStatus(uo.orderStatus) && uo.paymentMethod === 'ONLINE_PAYMENT')
      : autoPayOnlineFromCheckout || payFirstOnline;
    const fullyPaid =
      uo.paymentStatus === 'PAID' && (onlineBalanceDue == null || onlineBalanceDue <= 0);
    if (!needsPay || fullyPaid) {
      return;
    }
    autoPayKioskRef.current = true;
    void handlePayOnline();
  }, [
    appName,
    autoPayOnlineFromCheckout,
    handlePayOnline,
    kioskEnabled,
    orderDetails?.userOrder,
    paying,
    onlineBalanceDue,
  ]);

  const breadcrumbItems = appName
    ? kioskEnabled
      ? [
          { label: 'Menu', path: browseMenuPath },
          { label: 'Your Orders', path: kioskOrdersPath(appName) },
          { label: 'Order Details', active: true },
        ]
      : [
          { label: restaurantName, path: `/restaurants/${appName}` },
          { label: 'Menu', path: `/restaurants/${appName}/menu` },
          { label: 'Your Orders', path: `/restaurants/${appName}/my-orders` },
          { label: 'Order Details', active: true },
        ]
    : [{ label: 'Order Details', active: true }];

  const loginModal = (
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      title="Sign in to continue"
      message="Sign in to view your order status and details."
      onLoginSuccess={handleLoginSuccess}
    />
  );

  if (loading && !pendingEmailSignIn) {
    return (
      <div className="order-details-page">
        {!kioskEnabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
          <Breadcrumb items={breadcrumbItems} />
          <div className="order-details-container">
            <OrderDetailsSkeleton />
          </div>
          </div>
        </PageContainer>
        {!kioskEnabled && <Footer />}
        {loginModal}
      </div>
    );
  }

  if (pendingEmailSignIn && !orderDetails) {
    return (
      <div className="order-details-page">
        {!kioskEnabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
            <Breadcrumb items={breadcrumbItems} />
            <div className="order-details-container order-details-container--centered">
              <div className="order-details-signin-prompt">
                <div className="order-details-signin-prompt__icon" aria-hidden>
                  <i className="fa fa-user-circle" />
                </div>
                <h2>Sign in to view this order</h2>
                <p>Use the same account you ordered with to see status, items, and payment details.</p>
                <button type="button" className="order-details-signin-prompt__btn" onClick={() => setShowLoginModal(true)}>
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </PageContainer>
        {!kioskEnabled && <Footer />}
        {loginModal}
      </div>
    );
  }

  if (error || !orderDetails) {
    return (
      <div className="order-details-page">
        {!kioskEnabled && <Header />}
        <PageContainer>
          <div className="orders-page-shell">
          <Breadcrumb items={breadcrumbItems} />
          <div className="order-details-container order-details-container--centered">
            <OrderUnavailableState
              appName={appName}
              message={error || 'Order not found'}
            />
          </div>
          </div>
        </PageContainer>
        {!kioskEnabled && <Footer />}
        {loginModal}
      </div>
    );
  }

  const {
    userOrder,
    userOrderItemsList,
    menuCardList,
    userRestaurantTable,
    billingDetails,
    assignedUser,
    userDeliveryDetails,
    userOrderDeliveryDetails,
  } = orderDetails;
  const statusInfo = getStatusLabel(userOrder.orderStatus);
  let currencySymbol = userOrder.currencySymbol || '₹';
  if (currencySymbol && currencySymbol.includes('&#')) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currencySymbol;
    currencySymbol = tempDiv.textContent || tempDiv.innerText || '₹';
  }

  const tableNumber =
    userRestaurantTable?.tableNumber ?? userOrder.tableNumber;
  const spaceLabel =
    userRestaurantTable?.spaceLabel ||
    formatSpaceLabel(userRestaurantTable?.spaceType, tableNumber);

  const appliedPromotionDiscount =
    (userOrder.promotionDiscountAmount ?? 0) > 0
      ? userOrder.promotionDiscountAmount || 0
      : !isCompletedOrderStatus(userOrder.orderStatus)
        ? orderPromotionPreview?.estimatedDiscount || 0
        : billingDetails?.promotionDiscountAmount || 0;

  const itemsSubtotalFromLines =
    userOrderItemsList?.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0) ?? 0;

  const summarySubtotal =
    billingDetails?.subTotal ??
    userOrder.subTotal ??
    (itemsSubtotalFromLines > 0 ? itemsSubtotalFromLines : undefined);

  const displayGrandTotal =
    billingDetails?.grandTotal != null && billingDetails.grandTotal > 0
      ? billingDetails.grandTotal
      : userOrder.grandTotal;

  const paidPresentation = guestPaidPresentation({
    paymentStatus: userOrder.paymentStatus,
    paymentMethod: userOrder.paymentMethod,
    orderStatus: userOrder.orderStatus,
    onlinePaidAmount,
    onlineBalanceDue,
  });
  const isFullyPaidOnline = paidPresentation === 'full';
  const isPartiallyPaidOnline = paidPresentation === 'partial';

  const showOrderSummary =
    summarySubtotal != null ||
    displayGrandTotal != null ||
    (appliedPromotionDiscount ?? 0) > 0 ||
    Boolean(billingDetails);

  const showApplicableOffers =
    !isCompletedOrderStatus(userOrder.orderStatus) && applicableOffers.length > 0;

  const isDeliveryOrder = userOrder.orderType === 2;
  const dropLine =
    deliveryTracking?.drop?.line ||
    formatDeliveryAddressLine(userDeliveryDetails) ||
    userOrderDeliveryDetails?.destination ||
    '';
  const dropName = deliveryTracking?.drop?.fullName || userDeliveryDetails?.fullName;
  const payMethod = deliveryTracking?.payMethod || userOrder.paymentMethod;
  const paidMethodLabel = guestPaymentMethodLabel(userOrder.paymentMethod, userOrder.orderType);
  const trackStatus = deliveryTracking?.status;
  const paymentIncomplete = isPaymentIncompleteStatus(userOrder.orderStatus);
  const deliveryItemLines = (userOrderItemsList || []).map((item) => ({
    status: (item as { status?: string }).status,
    active: (item as { active?: boolean }).active !== false,
  }));
  const deliveryOrderStatus = isDeliveryOrder
    ? resolveDeliveryGuestOrderStatus(userOrder.orderStatus, trackStatus, deliveryItemLines)
    : userOrder.orderStatus;
  const trackStep = deliveryStepIndex(deliveryOrderStatus, trackStatus);
  const trackFailed = trackStep < 0 || paymentIncomplete;
  const showItemStatusBadges = Boolean(
    userOrderItemsList?.some((item) => {
      const status = (item as { status?: string }).status;
      return status && status !== 'PLACED';
    })
  );
  const itemGroups = guestItemGroups(userOrderItemsList || [], signedInUser?.id);

  const renderOrderItem = (item: OrderItem) => {
    const menuItem = menuCardList?.find((m) => m.id === item.menuId);
    const itemStatus = (item as { status?: string }).status;
    const itemPromo = item.menuId ? itemPromotionBadges?.[item.menuId] : undefined;
    return (
      <div key={item.id} className="items-row">
        <span className="item-name">
          {menuItem ? (
            <Link to={guestOrderingPath(`/restaurants/${appName}/menu/${menuItem.itemUrl}`)}>
              {item.itemName}
            </Link>
          ) : (
            item.itemName
          )}
          {itemPromo?.promotionBadge ? (
            <span className="od-item-promo-badge">{itemPromo.promotionBadge}</span>
          ) : null}
          {showItemStatusBadges && itemStatus ? (
            <span className={itemStatusBadgeClass(itemStatus)}>
              {itemStatusLabel(itemStatus)}
            </span>
          ) : null}
          <OrderLineOptions optionsNote={item.optionsNote} itemNote={item.itemNote} />
        </span>
        <span className="item-qty">×{item.quantity}</span>
        <span className="item-total">
          {formatCurrency(item.totalPrice, currencySymbol)}
        </span>
      </div>
    );
  };

  const showDineInCallCaptain =
    userOrder.orderType === DINE_IN_ORDER_TYPE &&
    !isTerminalOrderStatus(userOrder.orderStatus);
  const showJoinSharePin = Boolean(
    userOrder.isSharedOrder &&
      userOrder.sharePin &&
      !isTerminalOrderStatus(userOrder.orderStatus)
  );

  const renderCallAction = (className: string) => {
    if (showDineInCallCaptain) {
      return (
        <button
          type="button"
          className={className}
          onClick={() => requestCaptainAssistanceOpen()}
          aria-label="Open assistance to reach your captain"
        >
          <i className="fa fa-phone" aria-hidden />
          Call captain
        </button>
      );
    }
    if (!restaurantPhone) {
      return null;
    }
    return (
      <a
        className={className}
        href={restaurantTelHref(restaurantPhone)}
        aria-label={`Call ${restaurantName}`}
      >
        <i className="fa fa-phone" aria-hidden />
        Call restaurant
      </a>
    );
  };

  return (
    <div
      className={`order-details-page${
        bottomBarConfig.visible ? ' order-details-page--bottom-bar' : ''
      }`}
    >
      {!kioskEnabled && <Header />}
      <PageContainer>
        <div className="orders-page-shell">
            <Breadcrumb items={breadcrumbItems} />
        <div className="order-details-container">
          <div className="order-details-layout">
            <div className="od-main">
              <div className={`od-top-bar${isDeliveryOrder ? ' od-top-bar--delivery' : ''}`}>
                <div className="od-title-row">
                  <h1 className="od-title">{isDeliveryOrder ? 'Your delivery' : 'Order Details'}</h1>
                  {!isDeliveryOrder && (
                    <span className="type-badge">{getOrderTypeLabel(userOrder.orderType)}</span>
                  )}
                  {!isDeliveryOrder && (
                    <span className={`status-pill ${statusInfo.className}`}>
                      {statusInfo.label}
                    </span>
                  )}
                  {appName && isCompletedOrderStatus(userOrder.orderStatus) && (
                    <GuestInvoiceDownload
                      appName={appName}
                      orderId={userOrder.id}
                      invoiceDownload={invoiceDownload}
                    />
                  )}
                  {renderCallAction('od-call-btn')}
                </div>
                <div className="od-meta-row">
                  <div className="od-meta-item">
                    <i className="fa fa-hashtag" aria-hidden />
                    Order ID: <strong>#{userOrder.orderCode || userOrder.id}</strong>
                  </div>
                  {tableNumber != null && (
                    <div className="od-meta-item">
                      <i className="fa fa-cutlery" aria-hidden />
                      {spaceLabel}
                    </div>
                  )}
                  {assignedUser && (
                    <div className="od-meta-item">
                      <i className="fa fa-user" aria-hidden />
                      Captain: <strong>{assignedUser.userName}</strong>
                    </div>
                  )}
                  {userOrder.createdOn && (
                    <div className="od-meta-item">
                      <i className="fa fa-clock-o" aria-hidden />
                      <strong>{formatDate(userOrder.createdOn)}</strong>
                    </div>
                  )}
                  {(userOrder.orderStatus === 'ORDER_PREPARING' || userOrder.orderStatus === 'ORDER_CONFIRMED') &&
                    (userOrder.estimatedPrepMinutes != null || userOrder.approxWaitTime != null) && (
                    <div className="od-meta-item">
                      <i className="fa fa-hourglass-half" aria-hidden />
                      Est. wait: <strong>~{userOrder.estimatedPrepMinutes ?? userOrder.approxWaitTime} min</strong>
                    </div>
                  )}
                </div>
                {showJoinSharePin && (
                  <div className="od-share-pin">
                    <span className="pin-label">
                      <i className="fa fa-share-alt" aria-hidden /> Share PIN to join order:
                    </span>
                    <span className="pin-digits">{userOrder.sharePin}</span>
                    <button
                      type="button"
                      className="share-btn"
                      onClick={() =>
                        handleShareOrder(userOrder.sharePin!, userOrder.id, appName!)
                      }
                    >
                      <i className="fa fa-share" aria-hidden /> Share
                    </button>
                  </div>
                )}
              </div>

              {isDeliveryOrder && (
                <div className={`od-delivery-card${trackFailed ? ' od-delivery-card--alert' : ''}`}>
                  <div className="od-delivery-head">
                    <p className="od-delivery-kicker">Delivery status</p>
                    <p className="od-delivery-status">{deliveryHeadline(deliveryOrderStatus, trackStatus)}</p>
                    <p className="od-delivery-hint">{deliveryHint(deliveryOrderStatus, trackStatus)}</p>
                    {deliveryTracking?.expectedDeliverAt && trackStep >= 0 && trackStep < 7 && (
                      <p className="od-delivery-eta">
                        Arriving around <strong>{formatClock(deliveryTracking.expectedDeliverAt)}</strong>
                      </p>
                    )}
                    {deliveryTracking?.acceptDeadlineAt && trackStep === 0 && (
                      <p className="od-delivery-eta">
                        Restaurant usually responds by {formatClock(deliveryTracking.acceptDeadlineAt)}
                      </p>
                    )}
                  </div>
                  {!trackFailed && (
                    <div className="od-steps-scroll" ref={stepsScrollRef}>
                      <ol className="od-steps" aria-label="Delivery progress">
                        {DELIVERY_STEPS.map((step, index) => (
                          <li
                            key={step.key}
                            className={
                              index < trackStep ? 'is-done' : index === trackStep ? 'is-current' : ''
                            }
                          >
                            <span className="od-step-dot" aria-hidden />
                            <span className="od-step-label">{step.label}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {dropLine ? (
                    <div className="od-drop">
                      <i className="fa fa-map-marker" aria-hidden />
                      <div>
                        {dropName && <div className="od-drop-name">{dropName}</div>}
                        <p className="od-delivery-drop">{dropLine}</p>
                        {deliveryTracking?.drop?.instructions && (
                          <p className="od-drop-note">{deliveryTracking.drop.instructions}</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {payMethod === 'CASH_ON_DELIVERY' && (
                    <p className="od-delivery-pay">Pay cash to the rider</p>
                  )}
                  {(payMethod && payMethod !== 'CASH_ON_DELIVERY' && isFullyPaidOnline) && (
                    <p className="od-delivery-pay">Paid online</p>
                  )}
                  {(payMethod && payMethod !== 'CASH_ON_DELIVERY' && isPartiallyPaidOnline) && (
                    <p className="od-delivery-pay">
                      Paid {formatCurrency(onlinePaidAmount ?? 0, currencySymbol)} online ·{' '}
                      {formatCurrency(onlineBalanceDue ?? 0, currencySymbol)} due
                    </p>
                  )}
                </div>
              )}

              <div className="od-items-card">
                <div className="od-section-title">
                  Order Items
                  {userOrderItemsList && userOrderItemsList.length > 0 && (
                    <span className="od-section-count">
                      {userOrderItemsList.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                    </span>
                  )}
                </div>
                <div className="items-table">
                  <div className="items-thead">
                    <span>Item</span>
                    <span>Qty</span>
                    <span>Total</span>
                  </div>
                  {userOrderItemsList && userOrderItemsList.length > 0 ? (
                    itemGroups ? (
                      itemGroups.map((group) => (
                        <div key={group.key} className="od-item-guest">
                          <div className="od-item-guest__head">
                            <i className={`fa ${group.isStaff ? 'fa-cutlery' : 'fa-user'}`} aria-hidden />
                            <span>{group.label}</span>
                            {group.isYou ? <span className="od-item-guest__you">You</span> : null}
                            {group.isStaff && group.label !== 'Restaurant' ? (
                              <span className="od-item-guest__role">Restaurant</span>
                            ) : null}
                          </div>
                          {group.items.map(renderOrderItem)}
                        </div>
                      ))
                    ) : (
                      userOrderItemsList.map(renderOrderItem)
                    )
                  ) : (
                    <div className="items-row no-items-row">
                      <span className="item-name">No items in order</span>
                    </div>
                  )}
                </div>
                <OrderKitchenNote note={userOrder?.kitchenNote} className="od-kitchen-note" />
              </div>

              {showApplicableOffers && (
                <div className="od-offers-card">
                  <div className="od-section-title od-section-title-pad">Available Offers</div>
                  <div className="od-offers-list">
                    {(showAllOffers ? applicableOffers : applicableOffers.slice(0, 2)).map((offer) => (
                      <div key={offer.id} className="od-offer-item">
                        <div className="od-offer-badge">{offer.badge}</div>
                        <div className="od-offer-text">
                          <strong>{offer.label}</strong>
                          {offer.description ? ` - ${offer.description}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                  {applicableOffers.length > 2 && (
                    <button
                      className="od-offers-view-all"
                      onClick={() => setShowAllOffers((v) => !v)}
                    >
                      {showAllOffers
                        ? 'Show less'
                        : `View all ${applicableOffers.length} offers`}
                    </button>
                  )}
                  <p className="od-offers-billing-note">
                    Coupons and deals can be applied at billing. Ask staff when you request your bill.
                  </p>
                </div>
              )}

              {showOrderSummary && (
                <div className="od-summary-wrap">
                <div className="od-summary-card">
                  <div className="od-section-title od-section-title-pad">Order Summary</div>
                  {summarySubtotal != null && (
                    <div className="summary-row subtotal">
                      <span>Subtotal</span>
                      <span>{formatCurrency(summarySubtotal, currencySymbol)}</span>
                    </div>
                  )}
                  {(billingDetails?.discountAmount ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>
                        Discount
                        {(billingDetails?.discount ?? 0) > 0
                          ? ` (${billingDetails?.discount}%)`
                          : ''}
                      </span>
                      <span className="discount-val">
                        -{formatCurrency(billingDetails?.discountAmount || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(appliedPromotionDiscount ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>
                        Promotion
                        {orderPromotionPreview?.promotionName
                          ? `: ${orderPromotionPreview.promotionName}`
                          : ''}
                      </span>
                      <span className="discount-val">
                        −{formatCurrency(appliedPromotionDiscount || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.couponDiscountAmount ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>Coupon</span>
                      <span className="discount-val">
                        −{formatCurrency(billingDetails?.couponDiscountAmount || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.stateGST ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>SGST ({billingDetails?.stateGST}%)</span>
                      <span>
                        +{formatCurrency(billingDetails?.stateGSTCharges || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.centralGST ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>CGST ({billingDetails?.centralGST}%)</span>
                      <span>
                        +{formatCurrency(billingDetails?.centralGSTCharges || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.packingCharges ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>Packing Charges</span>
                      <span>
                        +{formatCurrency(billingDetails?.packingCharges ?? 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.deliveryCharges ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>Delivery Charges</span>
                      <span>
                        +{formatCurrency(billingDetails?.deliveryCharges ?? 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(billingDetails?.serviceTax ?? 0) > 0 && (
                    <div className="summary-row subtotal">
                      <span>Service Tax ({billingDetails?.serviceTax}%)</span>
                      <span>
                        +{formatCurrency(billingDetails?.serviceTaxCharges || 0, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {displayGrandTotal != null && (
                    <div className="summary-row total">
                      <span>Total Amount</span>
                      <span>{formatCurrency(displayGrandTotal, currencySymbol)}</span>
                    </div>
                  )}
                </div>
                {isPartiallyPaidOnline && (
                  <div className="od-paid-badge od-paid-badge--partial">
                    <i className="fa fa-adjust" aria-hidden />
                    <span>
                      Paid {formatCurrency(onlinePaidAmount ?? 0, currencySymbol)} online
                    </span>
                    <span className="od-paid-sep" aria-hidden>
                      {'\u00b7'}
                    </span>
                    <span className="od-paid-method">
                      {formatCurrency(onlineBalanceDue ?? 0, currencySymbol)} due
                    </span>
                  </div>
                )}
                {isFullyPaidOnline && (
                  <div className="od-paid-badge">
                    <i className="fa fa-check-circle" aria-hidden />
                    <span>Paid</span>
                    {paidMethodLabel ? (
                      <>
                        <span className="od-paid-sep" aria-hidden>
                          {'\u00b7'}
                        </span>
                        <span className="od-paid-method">{paidMethodLabel}</span>
                      </>
                    ) : null}
                  </div>
                )}
                {openPaymentId && (
                  <div className="od-payment-open">
                    <p>A payment for this order is still open. Cancel it before adding items or paying another way.</p>
                    <button type="button" onClick={handleCancelOpenPayment} disabled={cancellingPayment}>
                      {cancellingPayment ? 'Cancelling…' : 'Cancel payment'}
                    </button>
                  </div>
                )}
                {canPayOnline && !isCompletedOrderStatus(userOrder.orderStatus) && (
                  <button
                    type="button"
                    className="od-pay-btn"
                    onClick={handlePayOnline}
                    disabled={paying}
                  >
                    <i className="fa fa-lock" aria-hidden />
                    {paying
                      ? 'Opening payment…'
                      : `Pay ${formatCurrency(bottomBarConfig.payAmount || onlineBalanceDue || displayGrandTotal || userOrder.grandTotal || 0, currencySymbol)}`}
                  </button>
                )}
                </div>
              )}

              {feedbackSummary &&
                (feedbackSummary.eligible || feedbackSummary.submitted) &&
                isCompletedOrderStatus(userOrder.orderStatus) && (
                <>
                  {kioskEnabled && awaitingFeedbackPhase && (
                    <p className="order-details-kiosk-feedback-hint" role="status">
                      Please share your feedback below. This kiosk will sign out automatically in about 5
                      minutes, or tap Logout when you are done.
                    </p>
                  )}
                  {feedbackSummary.eligible && !feedbackSummary.submitted && (
                    <div className="order-feedback-banner">
                      <p>
                        <i className="fa fa-star" aria-hidden /> Your order is complete — how was your experience?
                      </p>
                      <button type="button" onClick={() => setShowFeedbackModal(true)}>
                        Rate your order
                      </button>
                    </div>
                  )}
                  <OrderFeedbackPanel
                    key={feedbackPanelKey}
                    appName={appName!}
                    orderId={parseInt(orderId!, 10)}
                    onSubmitted={handleFeedbackSubmitted}
                    onNotify={handleNotify}
                  />
                </>
              )}

              {/* Reward unlocked strip — shown on completed orders when loyalty rewards are enabled */}
              {!kioskEnabled &&
              hasCustomerFeature(planFeatures, 'loyalty_rewards') &&
              isAuthenticated &&
              isCompletedOrderStatus(userOrder.orderStatus) && (
                <div
                  style={{
                    margin: '16px 0',
                    padding: '14px 16px',
                    background: '#FFF5F3',
                    border: '1.5px solid #D9472B',
                    borderRadius: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                  onClick={() => navigate(`/restaurants/${appName}/rewards`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/restaurants/${appName}/rewards`)}
                >
                  <span style={{ fontSize: 28 }}>🎁</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#D9472B' }}>
                      Reward Unlocked!
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #6B5E3E)', marginTop: 2 }}>
                      A scratch card is waiting for you — tap to reveal your reward.
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: '#D9472B', fontWeight: 700 }}>→</span>
                </div>
              )}

              {!kioskEnabled &&
              hasCustomerFeature(planFeatures, 'in_app_games') &&
              isAuthenticated &&
              hasEnabledGames && (
                <div
                  style={{
                    margin: '16px 0',
                    padding: '14px 16px',
                    background: 'var(--primary-light, #FFFBEA)',
                    border: '1.5px solid var(--primary, #F5C21A)',
                    borderRadius: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                  onClick={() => navigate(`/restaurants/${appName}/games`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/restaurants/${appName}/games`)}
                >
                  <span style={{ fontSize: 28 }}>🎮</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary, #1F2428)' }}>
                      Play while you wait!
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #6B5E3E)', marginTop: 2 }}>
                      Spin the wheel or scratch a card to win a discount
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: 'var(--primary-dark, #D4A416)', fontWeight: 700 }}>→</span>
                </div>
              )}

              {menuRecommendations && (
                <div className="od-recommendations">
                  <MenuRecommendationsStrip
                    title="Frequently Bought Together"
                    items={menuRecommendations.frequentlyBoughtTogether}
                    appName={appName!}
                    restroId={userOrder.restroId}
                    variant="compact"
                    formatPrice={(price) => formatCurrency(price ?? 0, currencySymbol)}
                    onNavigate={(item: MenuItem) =>
                      navigate(
                        guestOrderingPath(
                          `/restaurants/${appName}/menu/${item.itemUrl || item.id}`
                        )
                      )
                    }
                  />
                  <MenuRecommendationsStrip
                    title="Best Selling Items"
                    subtitle="Guest favourites this week"
                    items={menuRecommendations.bestSelling}
                    appName={appName!}
                    restroId={userOrder.restroId}
                    variant="featured"
                    formatPrice={(price) => formatCurrency(price ?? 0, currencySymbol)}
                    onNavigate={(item: MenuItem) =>
                      navigate(
                        guestOrderingPath(
                          `/restaurants/${appName}/menu/${item.itemUrl || item.id}`
                        )
                      )
                    }
                  />
                  <MenuRecommendationsStrip
                    title="Today's Special"
                    subtitle="Chef's exclusive picks"
                    items={menuRecommendations.todaysSpecial}
                    appName={appName!}
                    restroId={userOrder.restroId}
                    variant="featured"
                    formatPrice={(price) => formatCurrency(price ?? 0, currencySymbol)}
                    onNavigate={(item: MenuItem) =>
                      navigate(
                        guestOrderingPath(
                          `/restaurants/${appName}/menu/${item.itemUrl || item.id}`
                        )
                      )
                    }
                  />
                </div>
              )}

              {!(kioskEnabled && awaitingFeedbackPhase) ? (
                <div className="od-actions">
                  {renderCallAction('od-call-btn od-call-btn--actions')}
                  <Link
                    to={guestOrderingPath(`/restaurants/${appName}/my-orders`)}
                    className="back-btn"
                  >
                    <i className="fa fa-arrow-left" aria-hidden /> Back to Orders
                  </Link>
                  <Link
                    to={guestOrderingPath(`/restaurants/${appName}/menu`)}
                    className="btn-back-to-menu"
                  >
                    Browse Menu
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        </div>
      </PageContainer>

      {bottomBarConfig.visible && (
        <div className="od-bottom-bar" role="region" aria-label="Order actions">
          <div
            className={`od-bottom-bar__inner${
              bottomBarConfig.showPay &&
              (bottomBarConfig.showCallCaptain || bottomBarConfig.showSharePin)
                ? ' od-bottom-bar__inner--stacked'
                : ''
            }`}
          >
            {bottomBarConfig.showPay && (
              <button
                type="button"
                className="od-bottom-bar__pay"
                onClick={handlePayOnline}
                disabled={paying}
              >
                <i className="fa fa-lock" aria-hidden />
                <span className="od-bottom-bar__pay-text">
                  {paying ? 'Opening payment…' : 'Pay'}
                </span>
                {!paying && (
                  <span className="od-bottom-bar__pay-amount">
                    {formatCurrency(bottomBarConfig.payAmount, currencySymbol)}
                  </span>
                )}
              </button>
            )}
            {(bottomBarConfig.showCallCaptain || bottomBarConfig.showSharePin) && (
              <div className="od-bottom-bar__secondary-row">
                {bottomBarConfig.showCallCaptain && (
                  <button
                    type="button"
                    className="od-bottom-bar__secondary"
                    onClick={() => requestCaptainAssistanceOpen()}
                    aria-label="Open assistance to reach your captain"
                  >
                    <i className="fa fa-phone" aria-hidden />
                    <span>Call captain</span>
                  </button>
                )}
                {bottomBarConfig.showSharePin && userOrder.sharePin && (
                  <button
                    type="button"
                    className="od-bottom-bar__secondary"
                    onClick={() => handleShareOrder(userOrder.sharePin!, userOrder.id, appName!)}
                    aria-label={`Share join link for PIN ${userOrder.sharePin}`}
                  >
                    <i className="fa fa-share-alt" aria-hidden />
                    <span>Share PIN</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!kioskEnabled && <Footer />}

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}

      {appName && orderId && (
        <OrderFeedbackModal
          appName={appName}
          orderId={parseInt(orderId, 10)}
          open={showFeedbackModal}
          onClose={handleDismissFeedbackModal}
          onSubmitted={handleFeedbackSubmitted}
          onNotify={handleNotify}
        />
      )}

      {loginModal}
    </div>
  );
};

export default OrderDetailsPage;
